/**
 * /staff/login — public
 *
 * Internal-staff login page. Distinct from customer /login — uses the
 * `internal_access_token` cookie, not the customer JWT.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "@/lib/api";
import { ShieldCheck } from "@phosphor-icons/react";

export default function StaffLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const r = await api.post("/internal/auth/login", { email, password });
      // Force-password-reset takes absolute priority — the staff member is
      // NOT allowed into the console until they set a new password.
      if (r?.data?.staff?.force_password_reset) {
        nav("/staff/change-password", { replace: true });
      } else {
        nav("/owner/staff", { replace: true });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[var(--bg-canvas)] flex items-center justify-center p-6" data-testid="staff-login-page">
      <div className="w-full max-w-md bg-white border border-[var(--border)] p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck size={22} className="text-emerald-600" />
          <div>
            <div className="text-[11px] font-mono tracking-widest text-[var(--text-muted)]">/ HELLO BY FLOW PLATFORM CONSOLE</div>
            <div className="text-[16px] font-semibold">Internal staff sign-in</div>
          </div>
        </div>

        {error && <div className="mb-4 text-red-700 text-[13px] p-3 bg-red-50 border border-red-200" data-testid="staff-login-error">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-mono text-[var(--text-muted)] tracking-widest mb-1">EMAIL</label>
            <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-[var(--border)]" data-testid="staff-login-email" />
          </div>
          <div>
            <label className="block text-[11px] font-mono text-[var(--text-muted)] tracking-widest mb-1">PASSWORD</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 border border-[var(--border)]" data-testid="staff-login-password" />
          </div>
          <button type="submit" disabled={busy} className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-[13px] disabled:opacity-50" data-testid="staff-login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-6 text-[11px] text-[var(--text-muted)]">
          Platform Owner? Use the standard <a href="/login" className="underline">customer login</a> instead.
        </div>
      </div>
    </div>
  );
}
